import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { MF_DISCLAIMER, COMMISSION_DISCLOSURE, NON_MF_NOTE } from '@/components/ComplianceNotice';

const Footer = () => {
  const whatsappNumber = '919841790000';
  const whatsappMessage = 'Hello, I would like to know more about your services.';

  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_wealth-journey-147/artifacts/xh3bugve_image.png"
              alt="Impetus Investment Services Logo"
              className="h-16 w-auto object-contain bg-white p-2 rounded-lg mb-4"
            />
            <h3 className="text-xl font-heading font-bold text-[#D97706] mb-2">
              IMPETUS INVESTMENT SERVICES
            </h3>
            <p className="text-sm text-gray-400 mb-4 font-body italic">
              Bridge to Financial Wellness
            </p>
            <p className="text-sm text-gray-400 font-body mb-2">
              AMFI-registered Mutual Fund Distributor
            </p>
            <p className="text-sm text-[#E8A428] font-body font-semibold">ARN: 116347</p>
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500 font-body mb-1 uppercase tracking-wider">Founders</p>
              <p className="text-sm text-gray-300 font-body">Sravanesh Amara</p>
              <p className="text-sm text-gray-300 font-body">Raghavan N.</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 font-body">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#D97706] transition">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-[#D97706] transition">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-[#D97706] transition">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/education" className="text-gray-400 hover:text-[#D97706] transition">
                  Investor Education
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-gray-400 hover:text-[#D97706] transition">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/appointment" className="text-gray-400 hover:text-[#D97706] transition">
                  Book Appointment
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Services</h4>
            <ul className="space-y-2 font-body">
              <li>
                <Link to="/services/mutual-funds" className="text-gray-400 hover:text-[#D97706] transition">
                  Mutual Funds
                </Link>
              </li>
              <li>
                <Link to="/services/fixed-deposits" className="text-gray-400 hover:text-[#D97706] transition">
                  Fixed Deposits
                </Link>
              </li>
              <li>
                <Link to="/services/insurance" className="text-gray-400 hover:text-[#D97706] transition">
                  Insurance
                </Link>
              </li>
              <li>
                <Link to="/services/bonds" className="text-gray-400 hover:text-[#D97706] transition">
                  Bonds
                </Link>
              </li>
              <li>
                <Link to="/services/real-estate" className="text-gray-400 hover:text-[#D97706] transition">
                  Real Estate
                </Link>
              </li>
              <li>
                <Link to="/services/portfolio-review" className="text-gray-400 hover:text-[#D97706] transition">
                  Portfolio Review
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-4 font-body">
              <li className="flex items-start">
                <MapPin size={20} className="text-[#D97706] mr-2 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  New No. 28, Old No. 70, Ground Floor, Spur Tank Road, Chetpet, Chennai - 600031
                </span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="text-[#D97706] mr-2 flex-shrink-0" />
                <div className="flex flex-col">
                  <a href="tel:+919841790000" className="text-gray-400 hover:text-[#D97706] transition text-sm">
                    98417 90000
                  </a>
                  <a href="tel:+917305835581" className="text-gray-400 hover:text-[#D97706] transition text-sm">
                    73058 35581
                  </a>
                </div>
              </li>
              <li className="flex items-center">
                <Mail size={18} className="text-[#D97706] mr-2 flex-shrink-0" />
                <a href="mailto:cs@impetusinvestments.in" className="text-gray-400 hover:text-[#D97706] transition text-sm">
                  cs@impetusinvestments.in
                </a>
              </li>
              <li className="flex items-center">
                <MessageCircle size={18} className="text-[#D97706] mr-2 flex-shrink-0" />
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#D97706] transition text-sm"
                  data-testid="whatsapp-link"
                >
                  WhatsApp Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="bg-[#A0451E]/10 border border-[#A0451E]/30 rounded-xl p-6 mb-6">
            <p className="text-sm text-gray-300 mb-2 font-body">
              <strong className="text-[#D97706]">Important Disclaimer:</strong>
            </p>
            <p className="text-sm text-gray-400 mb-2 font-body">
              {MF_DISCLAIMER}
            </p>
            <p className="text-sm text-gray-400 mb-2 font-body" data-testid="footer-commission-disclosure">
              {COMMISSION_DISCLOSURE}
            </p>
            <p className="text-sm text-gray-400 mb-2 font-body">{NON_MF_NOTE}</p>
            <p className="text-sm text-gray-400 font-body">
              The information provided on this website is for general educational and informational purposes. It should not be treated as a guarantee of returns or as personalised investment, legal or tax advice. Investment suitability depends on the investor's financial circumstances, objectives, risk profile and investment horizon.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4 text-sm font-body">
            <Link to="/disclosures" className="text-gray-400 hover:text-[#D97706] transition" data-testid="footer-disclosures-link">
              Disclosures & Investor Grievances
            </Link>
            <Link to="/privacy-policy" className="text-gray-400 hover:text-[#D97706] transition" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <a
              href="https://www.amfiindia.com/uploads/Revised_Codeof_Conductfor_Mutual_Fund_Distributors_April2022_57d91fe1c4.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#D97706] transition"
              data-testid="footer-amfi-coc-link"
            >
              AMFI Code of Conduct
            </a>
            <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D97706] transition" data-testid="footer-scores-link">
              SEBI SCORES
            </a>
          </div>

          {/* Copyright */}
          <div className="text-center">
            <p className="text-sm text-gray-400 font-body">
              © {new Date().getFullYear()} Impetus Investment Services · AMFI-registered Mutual Fund Distributor · ARN: 116347. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;